import { useState, useMemo, useEffect, useCallback } from 'react';
import type { Shapefile, ShapefileFilters } from '../types/shapefile';
import { shapefileService } from '../services/climate/spatial/ShapefileService';

interface UseShapefilesReturn {
  shapefiles: Shapefile[];
  filteredShapefiles: Shapefile[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filters: ShapefileFilters;
  setFilters: (filters: ShapefileFilters) => void;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  totalShapefiles: number;
  activeShapefiles: number;
  inactiveShapefiles: number;
  refreshShapefiles: () => Promise<void>;
  fetchShapefiles: (page?: number, customFilters?: ShapefileFilters, pageSize?: number) => Promise<void>;
  updateShapefileOptimistically: (shapefileId: string, updates: Partial<Shapefile>) => void;
  deleteShapefileOptimistically: (shapefileId: string) => void;
}

export const useShapefiles = (): UseShapefilesReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shapefiles, setShapefiles] = useState<Shapefile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ShapefileFilters>({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });

  // Fetch shapefiles from climate server
  const fetchShapefiles = useCallback(async (
    page: number = pagination.page,
    customFilters: ShapefileFilters = filters,
    pageSize: number = pagination.limit
  ) => {
    setLoading(true);
    setError(null);

    try {
      const options = {
        page,
        limit: pageSize,
        search: searchQuery || undefined,
        geometryType: customFilters.geometryType || undefined,
        isActive: customFilters.isActive, // Remove default true filter
      };

      const response = await shapefileService.getShapefiles(options);
      
      setShapefiles(response.shapefiles);
      setPagination(response.pagination);
    } catch (err) {
      console.error('useShapefiles: Failed to fetch shapefiles:', err);
      setError('Failed to load shapefiles. Please try again.');
      setShapefiles([]);
      setPagination({ page: 1, limit: 25, total: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters, pagination.page, pagination.limit]);

  // Initial load
  useEffect(() => {
    const initialLoad = async () => {
      setLoading(true);
      setError(null);

      try {
        const options = {
          page: 1,
          limit: 25,
          search: undefined,
          geometryType: undefined,
          isActive: undefined, // Show all shapefiles by default
        };

        const response = await shapefileService.getShapefiles(options);
        
        setShapefiles(response.shapefiles);
        setPagination(response.pagination);
      } catch (err) {
        console.error('useShapefiles: Initial load failed:', err);
        setError('Failed to load shapefiles. Please try again.');
        setShapefiles([]);
        setPagination({ page: 1, limit: 25, total: 0, totalPages: 0 });
      } finally {
        setLoading(false);
      }
    };

    initialLoad();
  }, []);

  // Refresh shapefiles (reload current page)
  const refreshShapefiles = useCallback(async () => {
    await fetchShapefiles(pagination.page, filters, pagination.limit);
  }, [fetchShapefiles, pagination.page, filters, pagination.limit]);

  // Apply local search and filters to the loaded shapefiles
  const filteredShapefiles = useMemo(() => {
    let result = shapefiles;

    // Apply local search if needed (server-side search is preferred)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(shapefile => {
        const name = shapefile.name?.toLowerCase() || '';
        const filename = shapefile.filename?.toLowerCase() || '';
        const geometryType = shapefile.geometryType?.toLowerCase() || '';
        
        return name.includes(query) ||
               filename.includes(query) ||
               geometryType.includes(query);
      });
    }

    return result;
  }, [shapefiles, searchQuery]);

  // Calculate statistics
  const totalShapefiles = pagination.total;
  const activeShapefiles = shapefiles.filter(shapefile => shapefile.isActive).length;
  const inactiveShapefiles = shapefiles.filter(shapefile => !shapefile.isActive).length;

  // Optimistic update methods
  const updateShapefileOptimistically = useCallback((shapefileId: string, updates: Partial<Shapefile>) => {
    setShapefiles(prevShapefiles => 
      prevShapefiles.map(shapefile => 
        shapefile.id === shapefileId 
          ? { ...shapefile, ...updates, updatedAt: new Date().toISOString() }
          : shapefile
      )
    );
  }, []);

  const deleteShapefileOptimistically = useCallback((shapefileId: string) => {
    setShapefiles(prevShapefiles => 
      prevShapefiles.map(shapefile => 
        shapefile.id === shapefileId 
          ? { ...shapefile, isActive: false, updatedAt: new Date().toISOString() }
          : shapefile
      )
    );
    
    // Update pagination counts
    setPagination(prevPagination => ({
      ...prevPagination,
      total: Math.max(0, prevPagination.total - 1),
    }));
  }, []);

  return {
    shapefiles,
    filteredShapefiles,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    pagination,
    totalShapefiles,
    activeShapefiles,
    inactiveShapefiles,
    refreshShapefiles,
    fetchShapefiles,
    updateShapefileOptimistically,
    deleteShapefileOptimistically,
  };
}; 